import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { WebSocketProvider } from "@/contexts/WebSocketContext";
import { BillingProvider } from "@/contexts/BillingContext";
import { UiLayoutProvider } from "@/contexts/UiLayoutContext";
import Login from "@/pages/Login";
import Registrazione from "@/pages/Registrazione";
import SuperAdmin from "@/pages/SuperAdmin";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import MappaSala from "@/pages/MappaSala";
import Prenotazioni from "@/pages/Prenotazioni";
import NuovoOrdine from "@/pages/NuovoOrdine";
import OrderEditor from "@/pages/OrderEditor";
import Menu from "@/pages/Menu";
import TerminaleCucina from "@/pages/TerminaleCucina";
import TerminaleBar from "@/pages/TerminaleBar";
import TerminalePizzeria from "@/pages/TerminalePizzeria";
import TerminaleCassa from "@/pages/TerminaleCassa";
import CassaDetail from "@/pages/CassaDetail";
import ContiTavoli from "@/pages/ContiTavoli";
import MenuPubblico from "@/pages/MenuPubblico";
import MenuSchedulePage from "@/pages/MenuSchedule";
import ScontriniFatture from "@/pages/ScontriniFatture";
import Utenti from "@/pages/Utenti";
import Stampanti from "@/pages/Stampanti";
import IntegrazionePrenova from "@/pages/IntegrazionePrenova";
import Report from "@/pages/Report";
import Statistiche from "@/pages/Statistiche";
import PiattiLibrary from "@/pages/PiattiLibrary";
import Impostazioni from "@/pages/Impostazioni";
import InsightsAI from "@/pages/InsightsAI";
import Billing from "@/pages/Billing";
import AuthCallback from "@/pages/AuthCallback";
import Terminali from "@/pages/Terminali";
import CustomerEntry from "@/pages/customer/CustomerEntry";
import CustomerCallback from "@/pages/customer/CustomerCallback";
import Privacy from "@/pages/Privacy";
import CookiesPage from "@/pages/CookiesPage";
import Termini from "@/pages/Termini";
import QrLogin from "@/pages/QrLogin";
import Menus from "@/pages/Menus";
import MenuDelGiorno from "@/pages/MenuDelGiorno";
import Guida from "@/pages/Guida";
import ComandeArchiviate from "@/pages/ComandeArchiviate";
import ChiamateArchivio from "@/pages/ChiamateArchivio";

const ProtectedRoute = ({ children, roles }) => {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen bg-anthracite flex items-center justify-center">
                <div className="text-gold font-display text-xl tracking-wider">
                    Caricamento...
                </div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    if (roles && !roles.includes(user.role) && user.role !== "admin") {
        return <Navigate to="/dashboard" replace />;
    }
    return children;
};

const RoleHome = () => {
    const { user } = useAuth();
    if (!user) return <Navigate to="/login" replace />;
    if (user.role === "kitchen") return <Navigate to="/cucina" replace />;
    if (user.role === "bar") return <Navigate to="/bar" replace />;
    if (user.role === "pizza") return <Navigate to="/pizzeria" replace />;
    if (user.role === "waiter") return <Navigate to="/mappa-sala" replace />;
    if (user.role === "cashier") return <Navigate to="/cassa" replace />;
    return <Navigate to="/dashboard" replace />;
};

const PublicLegalLayout = ({ children }) => (
    <div className="min-h-screen bg-deep text-warm">
        <div className="border-b border-[#1f2c44] bg-surface px-4 lg:px-8 py-4 flex items-center justify-between">
            <Link to="/" className="font-display text-xl font-bold text-warm">
                <span className="text-brand">Smart</span>Table
            </Link>
            <Link to="/login" className="text-sm text-elegant hover:text-brand">Accedi →</Link>
        </div>
        <div className="px-4 lg:px-8 py-10">{children}</div>
        <footer className="border-t border-[#1f2c44] bg-surface px-4 lg:px-8 py-4 text-[11px] text-elegant text-center">
            © 2026 MAKAI di Maria Laura Pellino · P.IVA 09936410969 · Tutti i diritti riservati.
        </footer>
    </div>
);

const GlobalCookieBanner = () => {
    const [show, setShow] = React.useState(false);
    React.useEffect(() => {
        if (!localStorage.getItem("cookie_consent_v1")) setShow(true);
    }, []);
    const accept = (level) => {
        localStorage.setItem("cookie_consent_v1", JSON.stringify({ level, at: new Date().toISOString() }));
        setShow(false);
    };
    if (!show) return null;
    // Nasconde sul percorso /c (portale clienti) e /m/ (menu pubblico) per non disturbare il cliente
    if (window.location.pathname.startsWith("/c") || window.location.pathname.startsWith("/m/")) return null;
    return (
        <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-surface border-t-2 border-brand p-4 lg:p-6 backdrop-blur-md" data-testid="cookie-banner">
            <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-start lg:items-center gap-4">
                <div className="flex-1 text-sm">
                    <div className="font-display font-bold text-warm mb-1">🍪 Privacy & Cookie</div>
                    <div className="text-elegant text-xs">
                        Smart Table utilizza solo cookie tecnici essenziali (sessione, autenticazione) necessari al funzionamento del gestionale.
                        Non utilizziamo cookie di profilazione né strumenti di tracciamento di terze parti.
                        Per maggiori informazioni leggi la <Link to="/privacy" className="text-brand hover:underline">Privacy Policy</Link> e la <Link to="/cookies" className="text-brand hover:underline">Cookie Policy</Link>.
                    </div>
                </div>
                <div className="flex gap-2 flex-shrink-0 w-full lg:w-auto">
                    <button onClick={() => accept("technical")} data-testid="cookie-accept-technical"
                        className="flex-1 lg:flex-none h-11 px-5 border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded-md text-xs uppercase tracking-wider">
                        Solo essenziali
                    </button>
                    <button onClick={() => accept("all")} data-testid="cookie-accept-all"
                        className="flex-1 lg:flex-none h-11 px-5 bg-brand hover:bg-brand-light text-deep font-bold rounded-md text-xs uppercase tracking-wider">
                        Accetta tutti
                    </button>
                </div>
            </div>
        </div>
    );
};

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <WebSocketProvider>
                    <BillingProvider>
                    <UiLayoutProvider>
                    <BrowserRouter>
                        <GlobalCookieBanner />
                        <Toaster
                            position="top-right"
                            theme="dark"
                            toastOptions={{
                                style: {
                                    background: "#1a1a1a",
                                    border: "1px solid #2a2a2a",
                                    color: "#f5f5f0",
                                },
                            }}
                        />
                        <Routes>
                            <Route path="/login" element={<Login />} />
                            <Route path="/registrazione" element={<Registrazione />} />
                            <Route path="/superadmin" element={<SuperAdmin />} />
                            <Route path="/qr-login" element={<QrLogin />} />
                            <Route path="/auth/callback" element={<AuthCallback />} />
                            <Route path="/c" element={<CustomerEntry />} />
                            <Route path="/c/auth/callback" element={<CustomerCallback />} />
                            <Route path="/m/:shortCode" element={<MenuPubblico />} />
                            <Route path="/privacy" element={<PublicLegalLayout><Privacy /></PublicLegalLayout>} />
                            <Route path="/cookies" element={<PublicLegalLayout><CookiesPage /></PublicLegalLayout>} />
                            <Route path="/termini" element={<PublicLegalLayout><Termini /></PublicLegalLayout>} />
                            <Route
                                element={
                                    <ProtectedRoute>
                                        <Layout />
                                    </ProtectedRoute>
                                }
                            >
                                <Route path="/" element={<RoleHome />} />
                                <Route path="/dashboard" element={<ProtectedRoute roles={["admin", "cashier"]}><Dashboard /></ProtectedRoute>} />
                                <Route path="/insights" element={<ProtectedRoute roles={["admin", "cashier"]}><InsightsAI /></ProtectedRoute>} />
                                <Route path="/mappa-sala" element={<MappaSala />} />
                                <Route path="/prenotazioni" element={<Prenotazioni />} />
                                <Route path="/nuovo-ordine" element={<NuovoOrdine />} />
                                <Route path="/ordine/:tableId" element={<OrderEditor />} />
                                <Route path="/menu" element={<Menu />} />
                                <Route path="/menus" element={<ProtectedRoute roles={["admin", "cashier"]}><Menus /></ProtectedRoute>} />
                                <Route path="/menu-schedule" element={<ProtectedRoute roles={["admin"]}><MenuSchedulePage /></ProtectedRoute>} />
                                <Route path="/menu-del-giorno" element={<ProtectedRoute roles={["admin", "cashier"]}><MenuDelGiorno /></ProtectedRoute>} />
                                <Route path="/cucina" element={<TerminaleCucina />} />
                                <Route path="/bar" element={<TerminaleBar />} />
                                <Route path="/pizzeria" element={<TerminalePizzeria />} />
                                <Route path="/cassa" element={<ProtectedRoute roles={["admin", "cashier"]}><TerminaleCassa /></ProtectedRoute>} />
                                <Route path="/cassa/:tableId" element={<ProtectedRoute roles={["admin", "cashier"]}><CassaDetail /></ProtectedRoute>} />
                                <Route path="/conti-tavoli" element={<ProtectedRoute roles={["admin", "cashier", "waiter"]}><ContiTavoli /></ProtectedRoute>} />
                                <Route path="/scontrini" element={<ProtectedRoute roles={["admin", "cashier"]}><ScontriniFatture /></ProtectedRoute>} />
                                <Route path="/utenti" element={<ProtectedRoute roles={["admin"]}><Utenti /></ProtectedRoute>} />
                                <Route path="/stampanti" element={<ProtectedRoute roles={["admin"]}><Stampanti /></ProtectedRoute>} />
                                <Route path="/prenova" element={<ProtectedRoute roles={["admin"]}><IntegrazionePrenova /></ProtectedRoute>} />
                                <Route path="/billing" element={<ProtectedRoute roles={["admin"]}><Billing /></ProtectedRoute>} />
                                <Route path="/terminali" element={<ProtectedRoute roles={["admin"]}><Terminali /></ProtectedRoute>} />
                                <Route path="/report" element={<ProtectedRoute roles={["admin", "cashier"]}><Report /></ProtectedRoute>} />
                                <Route path="/statistiche" element={<ProtectedRoute roles={["admin"]}><Statistiche /></ProtectedRoute>} />
                                <Route path="/piatti" element={<ProtectedRoute roles={["admin","cashier"]}><PiattiLibrary /></ProtectedRoute>} />
                                <Route path="/impostazioni" element={<ProtectedRoute roles={["admin"]}><Impostazioni /></ProtectedRoute>} />
                                <Route path="/guida" element={<Guida />} />
                                <Route path="/comande-archiviate" element={<ProtectedRoute roles={["admin", "cashier", "waiter", "kitchen", "bar", "pizza"]}><ComandeArchiviate /></ProtectedRoute>} />
                                <Route path="/chiamate-archivio" element={<ProtectedRoute roles={["admin", "cashier", "waiter"]}><ChiamateArchivio /></ProtectedRoute>} />
                            </Route>
                            <Route path="*" element={<Navigate to="/" />} />
                        </Routes>
                    </BrowserRouter>
                    </UiLayoutProvider>
                    </BillingProvider>
                </WebSocketProvider>
            </AuthProvider>
        </div>
    );
}

export default App;
